import { useEffect, useState } from 'react';
import tenant from '../config/tenant';


const CACHE_LIMIT = 60;

const cache = new Map();

const inFlight = new Map();

const AUTH_KEYS = ['AuthKey', 'auth-key', 'auth_key', 'token', 'Authkey'];

export function getAuthKey() {
  for (const k of AUTH_KEYS) {
    try {
      const v = localStorage.getItem(k);
      if (v) return v;
    } catch {
      /* storage unavailable */
    }
  }
  return '';
}

export function needsAuth(url) {
  if (!url || typeof url !== 'string') return false;
  if (/^(data:|blob:)/i.test(url)) return false;

  const base = String(tenant?.api?.baseUrl || '').replace(/\/+$/, '');
  const imgBase = String(tenant?.api?.employeeImageBaseUrl || '').replace(/\/+$/, '');

  if (base && url.startsWith(base)) return true;
  if (imgBase && url.startsWith(imgBase)) return true;

 
  return !/^https?:\/\//i.test(url);
}

function remember(url, blobUrl) {
  cache.set(url, { blobUrl, at: Date.now() });

  while (cache.size > CACHE_LIMIT) {

    const oldest = cache.keys().next().value;
    const entry = cache.get(oldest);
    cache.delete(oldest);
    try { URL.revokeObjectURL(entry.blobUrl); } catch { }
  }
}

export async function fetchAuthedBlobUrl(url, { fresh = false } = {}) {
  if (!url) return null;
  if (!needsAuth(url)) return url;

  if (!fresh) {
    const hit = cache.get(url);
    if (hit) return hit.blobUrl;
    if (inFlight.has(url)) return inFlight.get(url);
  }

  const request = (async () => {
    const key = getAuthKey();
    const res = await fetch(url, {
      method: 'GET',
      headers: key ? { 'auth-key': key } : {},
      // Never answer from the browser's HTTP cache — the server may have a
      // newer file under the same name.
      cache: 'no-store',
    });

    if (!res.ok) {
      const err = new Error(
        res.status === 401
          ? 'Not authorised to read this document - the session may have expired.'
          : `Could not load the document (HTTP ${res.status}).`
      );
      err.status = res.status;
      throw err;
    }

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    remember(url, blobUrl);
    return blobUrl;
  })().finally(() => inFlight.delete(url));

  inFlight.set(url, request);
  return request;
}


export function useAuthedFileUrl(url) {
  const [state, setState] = useState({ src: null, loading: !!url, error: '' });

  useEffect(() => {
    let cancelled = false;

    if (!url) {
      setState({ src: null, loading: false, error: '' });
      return undefined;
    }

    if (!needsAuth(url)) {
      setState({ src: url, loading: false, error: '' });
      return undefined;
    }

    setState({ src: null, loading: true, error: '' });

    fetchAuthedBlobUrl(url)
      .then((src) => { if (!cancelled) setState({ src, loading: false, error: '' }); })
      .catch((err) => {
        console.error('[authedFile]', url, err);
        if (!cancelled) setState({ src: null, loading: false, error: err.message || 'Failed to load' });
      });


    return () => { cancelled = true; };
  }, [url]);

  return state;
}

export async function downloadAuthedFile(url, fileName = 'document') {
  if (!url) return;

  const blobUrl = await fetchAuthedBlobUrl(url, { fresh: true });
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}


export function clearAuthedFileCache() {
  cache.forEach(({ blobUrl }) => {
    try { URL.revokeObjectURL(blobUrl); } catch {  }
  });
  cache.clear();
  inFlight.clear();
}